import { PaginatedFieldsContext } from '@cibo/landmanager'
import { forceCommitInvalidate, useAllProgramContent, useForceCommitField } from '@cibo/programs'
import { FriendlyError, ResponsiveDialog } from '@cibo/ui'
import BugReportRounded from '@mui/icons-material/BugReportRounded'
import WarningIcon from '@mui/icons-material/WarningAmberOutlined'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  AlertTitle,
  Button,
  ButtonProps,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const BulkForceEnrollButton = (props: ButtonProps) => {
  const { t } = useTranslation('impact/EnrollSelectedFields')
  const { selectedFields, selectedRowIds } = useContext(PaginatedFieldsContext)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [programId, setProgramId] = useState('')
  const queryClient = useQueryClient()

  const programs = useAllProgramContent()

  const forceCommitField = useForceCommitField(() => null)

  const handleClickBulkEnroll = () => {
    Promise.all(
      selectedRowIds.map(resourceId =>
        forceCommitField.mutateAsync({ programId, fieldId: resourceId as string }).then(() => {
          setPickerOpen(false)
        })
      )
    ).then(() => {
      forceCommitInvalidate(queryClient, programId, selectedRowIds as string[])
    })
  }

  const canForceCommit = selectedFields?.length

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <LoadingButton
          color="secondary"
          variant="outlined"
          {...props}
          onClick={() => setPickerOpen(true)}
          disabled={!canForceCommit}
          startIcon={<BugReportRounded color="error" />}
        >
          Bulk Force Enroll
        </LoadingButton>
      </Stack>

      <ResponsiveDialog
        fullWidth
        maxWidth="sm"
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
      >
        <DialogTitle>Force Enroll Fields</DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            <Alert
              severity="warning"
              icon={<WarningIcon color="warning" sx={{ mt: '15px', mx: '9px' }} />}
            >
              <AlertTitle>Let's double check...</AlertTitle>
              Are you sure you want to force enroll {selectedRowIds.length} field(s)?
            </Alert>

            <Typography>Select program:</Typography>
            <Select onChange={event => setProgramId(event.target.value)} value={programId}>
              {programs.data?.map(program => (
                <MenuItem key={program.programId} value={program.programId}>
                  {program.content.fields.title}
                </MenuItem>
              ))}
            </Select>

            {!!forceCommitField.error && (
              <FriendlyError message={JSON.stringify(forceCommitField.error)} />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>{t('cancel')}</Button>
          <LoadingButton
            onClick={handleClickBulkEnroll}
            loading={forceCommitField.isLoading}
            variant="contained"
            color="secondary"
            disabled={!programId}
          >
            Force Enroll Fields
          </LoadingButton>
        </DialogActions>
      </ResponsiveDialog>
    </>
  )
}
